import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Banner, Button, Dialog, useSnackbar } from '@meshble/molo';
import { api, must } from '../lib/api';
import { usePermessi } from '../session';
import { Anello } from '../brand';
import { Info } from '../icons';
import { t } from '../lib/lingua';

interface ServerIP {
  id: string;
  name: string;
  provider: string;
  status: string;
  ipv4?: string;
  failoverIpv4?: string;
  failoverIpOrdered?: boolean;
}

/**
 * L'IP fisso dal lato del server: un server pronto che vive sull'IP della
 * macchina può averne uno con un clic — Molo lo compra su OVH, lo instrada
 * alla macchina che c'è già e ripunta lì i nomi delle istanze. Mentre OVH lo
 * consegna, lo dice.
 *
 * Comprare è di owner e admin, come assegnare un IP: sposta il traffico di
 * un cliente, e costa.
 */
export function IPFissoServer({ slug, server: s, vaiAlJob }: { slug: string; server: ServerIP; vaiAlJob?: () => void }) {
  // Un'offerta d'acquisto a chi non può comprare è rumore: qui il banner
  // intero sparisce, invece del solo pulsante disabilitato.
  const amministra = usePermessi(slug)('ip.manage');
  const [conferma, setConferma] = useState(false);

  if (s.failoverIpOrdered) {
    return (
      <Banner
        tone="info"
        icon={<Anello />}
        title={t('OVH sta consegnando l’IP fisso')}
        action={vaiAlJob && <Button variant="text" onClick={vaiAlJob}>{t('Segui')}</Button>}
      >
        {t('Di solito bastano pochi minuti. Appena arriva, Molo lo mette sulla macchina e ci ripunta i nomi delle istanze.')}
      </Banner>
    );
  }
  const puo = s.status === 'ready' && !s.failoverIpv4 && (s.provider === 'ovh' || s.provider === 'custom_ssh');
  if (!puo || !amministra) return null;

  return (
    <>
      <Banner
        tone="info"
        icon={<Info />}
        title={t('Questo server vive sull’IP della macchina')}
        action={<Button variant="text" onClick={() => setConferma(true)}>{t('Dai un IP fisso')}</Button>}
      >
        {t('Se la macchina si perde, l’indirizzo va con lei. Un IP fisso resta al servizio: in una DR passa al server nuovo senza toccare il DNS.')}
      </Banner>
      {conferma && <ConfermaAcquisto slug={slug} server={s} onClose={() => setConferma(false)} />}
    </>
  );
}

function ConfermaAcquisto({ slug, server: s, onClose }: { slug: string; server: ServerIP; onClose: () => void }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const navigate = useNavigate();

  const compra = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs/{slug}/servers/{id}/failover-ip', { params: { path: { slug, id: s.id } } })),
    onSuccess: (srv) => {
      void qc.invalidateQueries({ queryKey: ['servers', slug] });
      void qc.invalidateQueries({ queryKey: ['server', slug, s.id] });
      void qc.invalidateQueries({ queryKey: ['ips', slug] });
      onClose();
      avvisa(t('IP fisso chiesto per {nome}: segui la consegna in Attività', { nome: srv.name }));
      if (srv.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: srv.jobId } });
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={t('Dai un IP fisso a {nome}', { nome: s.name })}
      description={t('Molo compra su OVH un Additional IP geolocalizzato in Italia, 1,99 € al mese, instradato alla macchina che il server ha già. Niente si ferma.')}
      actions={
        <>
          <Button variant="text" onClick={onClose}>{t('Annulla')}</Button>
          <Button variant="filled" icon={compra.isPending ? <Anello /> : undefined} disabled={compra.isPending} onClick={() => compra.mutate()}>
            {compra.isPending ? t('Chiedo…') : t('Compra e assegna')}
          </Button>
        </>
      }
    >
      <div className="md-form">
        {compra.error && <Banner tone="error" title={t('Non comprato')}>{(compra.error as Error).message}</Banner>}
        <Banner tone="info" title={t('Cosa cambia per i clienti')}>
          {t('I nomi delle istanze che gestisce Molo passano da soli sull’IP fisso. Chi ha puntato un proprio dominio su {ip} deve spostarlo sul nuovo IP una volta: da lì in poi non cambierà più. Se il server viene dismesso, l’IP resta nel progetto, libero.', {
            ip: s.ipv4 ?? t('l’IP della macchina'),
          })}
        </Banner>
      </div>
    </Dialog>
  );
}
